import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { CKEditor5 } from '@ckeditor/ckeditor5-angular';
import { MtnaInlineEditorAction } from '@mtna/core-angular';
import { MtnaLanguageIds } from '@mtna/core-i18n-angular';
import { BehaviorSubject, Observable } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const require: any;

// This is necessary, as linter doesn't like the import * from this node module
// eslint-disable-next-line @typescript-eslint/no-var-requires
const ClassicEditor = require('@ckeditor/ckeditor5-build-classic');

/**
 * A component for editing rich text; capable of outputting HTML in string format.
 * @note Documentation for the ckeditor can be found here: https://ckeditor.com/docs/ckeditor5/latest/installation/index.html
 * @remarks In order to tailor to a specific language, a script must be added in project.json build for the desired language;
 * @example - French
 * "build": {
 *    "options": {
 *      "scripts": ["node_modules/@ckeditor/ckeditor5-build-classic/build/translations/fr.js"]
 *    }
 * }
 * @example - German
 * "build": {
 *    "options": {
 *      "scripts": ["node_modules/@ckeditor/ckeditor5-build-classic/build/translations/de.js"]
 *    }
 * }
 */
@Component({
  selector: 'rds-c-rich-text-editor',
  templateUrl: 'rich-text-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsCRichTextEditorComponent {
  static ngAcceptInputType_applying: BooleanInput;
  static ngAcceptInputType_hideEditorControls: BooleanInput;
  static ngAcceptInputType_readonly: BooleanInput;
  static ngAcceptInputType_value: string | null | undefined;
  static ngAcceptInputType_valueObservable: Observable<string | undefined> | null | undefined;

  /** required to hook up the ckeditor in the template. DO NOT USE */
  Editor = ClassicEditor;

  /** Whether changes are being applied. */
  @Input()
  get applying(): boolean {
    return this._applying;
  }
  set applying(applying: boolean) {
    this._applying = coerceBooleanProperty(applying);
  }
  private _applying = false;

  /**
   * Whether to hide the editor controls. Defaults to false.
   * @note This will cause the editor to apply changes on blur
   */
  @Input()
  get hideEditorControls(): boolean {
    return this._hideEditorControls;
  }
  set hideEditorControls(hideEditorControls: boolean) {
    this._hideEditorControls = coerceBooleanProperty(hideEditorControls);
  }
  private _hideEditorControls = false;

  /**
   * MtnaLanguageId that this editor represent. Defaults to 'en'
   */
  @Input()
  get languageId(): typeof MtnaLanguageIds.type {
    return this._languageId;
  }
  set languageId(languageId: typeof MtnaLanguageIds.type) {
    this._languageId = languageId ?? 'en';
    this._setEditorConfigLanguage();
  }
  private _languageId: typeof MtnaLanguageIds.type = 'en';

  /** Placeholder text in the content field of the editor. Defaults to 'Enter the content here.' */
  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(p: string) {
    this._placeholder = p;
    this._editorConfig = { ...this._editorConfig, placeholder: p };
  }
  private _placeholder = $localize`Enter the content here.`;

  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(readonly: boolean) {
    this._readonly = coerceBooleanProperty(readonly);
  }
  private _readonly = false;

  @Input()
  get value(): string {
    return this._value;
  }
  set value(value: string) {
    this._value = value ?? '';
    this._internalValue = value ?? '';
    this.cdr.markForCheck();
  }
  private _value = '';
  _internalValue = '';

  /**
   * FOR USE IN I18N EDITOR ONLY.
   * A BehaviorSubject to subscribe to for value changes.
   * Due to Angular's internal code, inputs only change when the primitive value or reference changes.
   * Even though this component updates this.value internally, the input doesn't appear to trigger when
   * the original value is re-input externally, i.e. on a value reset, so we must instead take this Subject and force input update internally.
   */
  @Input()
  get valueObservable() {
    return this._valueObservable;
  }
  set valueObservable(subject) {
    if (this._valueObservable) {
      this._valueObservable.unsubscribe();
    }
    this._valueObservable = subject;
    if (this._valueObservable) {
      this._valueObservable.subscribe((value) => (this.value = value ?? ''));
    }
  }
  private _valueObservable: BehaviorSubject<string | undefined> | undefined;

  /** Internal configuration for the rich text editor. DO NOT USE */
  _editorConfig: CKEditor5.Config = {
    placeholder: this._placeholder,
    // TODO: allow external heading configuration; input or token or both?
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
        { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
        { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
      ],
    },
    toolbar: [
      'undo',
      'redo',
      '|',
      'heading',
      '|',
      'bold',
      'italic',
      '|',
      'bulletedList',
      'numberedList',
      'blockQuote',
      '|',
      'outdent',
      'indent',
      '|',
    ],
  };

  @Output() valueChange = new EventEmitter<string>();

  constructor(protected cdr: ChangeDetectorRef) {}

  /**
   * Internal Function for connecting the rich text editor. DO NOT USE.
   */
  _onReady(editor: typeof ClassicEditor) {
    editor.ui.getEditableElement().parentElement.insertBefore(editor.ui.view.toolbar.element, editor.ui.getEditableElement());
    // If we set the readonly property on disabled, keyboard access is negated for the entire editor which is not desired.
    // Therefore, we set the readonly state of the ckeditor since it does not have an internal input for this.
    if (this.readonly) {
      editor.enableReadOnlyMode('rich-text-editor');
    } else {
      editor.disableReadOnlyMode('rich-text-editor');
    }
  }

  /** Handle value changes from the rich text editor */
  changeValue(action: MtnaInlineEditorAction) {
    if (action === 'APPLY') {
      this.value = this._internalValue;
      this.valueChange.emit(this._internalValue);
    } else {
      this._internalValue = `${this.value}`;
    }
  }

  protected _setEditorConfigLanguage() {
    this._editorConfig = { ...this._editorConfig, language: this.languageId };
  }
}
